import { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import Loader from '../../../../../../components/loader/index';
import { useReactForm } from '../../../../../../hooks/index';
import { stepContentByStep } from '../../../constants/step-mapper';
import { PopupType } from '../../../enums/popup-type.const';
import { useCreateTemplate } from '../../../hooks/use-create-template.hook';
import { useStepThreeSubmitState } from '../../../hooks/use-step-three-submit-state.hook';
import { useTemplateFormResets } from '../../../hooks/use-template-form-resets.hook';
import { TemplateFormStore } from '../../../store/template-form.store';
import { TemplatePopupStore } from '../../../store/template-popup.store';
import { getStepOneNameError } from '../../../types/template.types';
import {
  type AddTemplateFormSchema,
  getAddTemplateSchemaForStep,
} from '../../../validations/add-template.validation';
import { MapFieldsModal } from '../map-fields-modal/map-fields-modal';

const ADD_TEMPLATE_DEFAULT_VALUES: AddTemplateFormSchema = {
  stepOne: {
    name: '',
    category: undefined,
    subCategory: undefined,
    equityPlan: undefined,
    expireDocument: false,
    expirationPeriod: undefined,
  },
  stepTwo: {
    signatories: [],
    signingMode: undefined,
    signingOrderMatters: false,
  },
  stepThree: {
    file: undefined,
    fieldMapping: {},
    fieldMappingTypes: {},
    detectedVariables: [],
    tabs: [],
    fileParsed: false,
    mergeFieldIssues: [],
  },
};

const STEP_KEYS: Record<number, keyof AddTemplateFormSchema> = {
  1: 'stepOne',
  2: 'stepTwo',
  3: 'stepThree',
};

export const AddTemplateForm: FC = () => {
  const { createTemplate, isPending } = useCreateTemplate();
  const currentStep = TemplateFormStore(({ currentStep }) => currentStep);
  const setStep = TemplateFormStore(({ setStep }) => setStep);
  const totalSteps = TemplateFormStore(({ totalSteps }) => totalSteps);
  const toggleState = TemplateFormStore(({ toggleState }) => toggleState);
  const popupOpen = TemplatePopupStore(({ open }) => open);
  const popupType = TemplatePopupStore(({ type }) => type);
  const handlePopupDismiss = TemplatePopupStore(({ handleDismiss }) => handleDismiss);

  const schema = useMemo(() => getAddTemplateSchemaForStep(currentStep), [currentStep]);

  const {
    clearErrors,
    control,
    formState,
    formState: { errors },
    getFieldState,
    getValues,
    handleSubmit,
    setError,
    setValue,
    trigger,
    watch,
  } = useReactForm({ defaultValues: ADD_TEMPLATE_DEFAULT_VALUES, schema });

  useTemplateFormResets({ defaultValues: ADD_TEMPLATE_DEFAULT_VALUES, setStep, setValue, watch });

  const watchValues = watch();
  const schemaForCurrentStep = useMemo(
    () => getAddTemplateSchemaForStep(currentStep),
    [currentStep],
  );
  const isCurrentStepValid = useMemo(() => {
    const zodOk = schemaForCurrentStep.safeParse(watchValues).success;
    if (!zodOk) return false;
    if (currentStep === 1 && getStepOneNameError(errors) != null) return false;
    return true;
  }, [schemaForCurrentStep, watchValues, currentStep, errors]);

  const { isNextOrSubmitDisabled } = useStepThreeSubmitState({
    control,
    currentStep,
    isValid: isCurrentStepValid,
    mode: 'create',
  });

  const isLastStep = currentStep === 4;

  const handleNext = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    const stepKey = STEP_KEYS[currentStep];
    if (!stepKey) return;
    const valid = await trigger(stepKey);
    if (!valid) return;
    if (currentStep === 1 && getFieldState('stepOne.name', formState).error != null) return;
    setStep(currentStep + 1);
  };

  const onSubmit = (data: AddTemplateFormSchema) => {
    createTemplate(data, { onSuccess: () => toggleState(false) });
  };

  const handlePrev = () => {
    if (currentStep === 3) {
      clearErrors([
        'stepThree.file',
        'stepThree.fileParsed',
        'stepThree.fieldMapping',
        'stepThree.mergeFieldIssues',
      ]);
      setValue('stepThree.mergeFieldIssues', [], { shouldValidate: false });
    }
    setStep(currentStep - 1);
  };

  return (
    <form
      className="flex h-full flex-col overflow-hidden bg-gray-25"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex w-full items-center gap-[5px] bg-white px-6 pt-5">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            className={twMerge(
              'h-1 w-full rounded-[25px]',
              i + 1 <= currentStep ? 'bg-[#12B76A]' : 'bg-gray-100',
              i + 1 === currentStep &&
                currentStep === totalSteps &&
                'bg-gradient-to-r from-forest-500 to-forest-300',
            )}
            key={i}
          />
        ))}
      </div>

      <div className="flex h-full flex-col gap-4 overflow-hidden bg-gray-100">
        {stepContentByStep[currentStep]({
          clearErrors,
          control,
          getFieldError: () => getStepOneNameError(errors),
          getValues,
          setError,
          setValue,
        })}
      </div>

      {popupOpen && popupType === PopupType.MAP_FIELDS && (
        <MapFieldsModal
          canSave={isCurrentStepValid}
          control={control}
          isOpen
          mode="create"
          onClose={handlePopupDismiss}
          setValue={setValue}
        />
      )}

      <div className="flex items-center justify-between bg-gray-100 px-6 py-4">
        <button
          className="text-sm font-medium text-gray-700 hover:text-gray-900"
          onClick={() => toggleState(false)}
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </button>
        <div className="flex gap-3">
          <Button
            className="border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            disabled={currentStep === 1}
            onClick={handlePrev}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>
          <Button
            className="bg-brand-700 px-6 py-2 text-sm font-medium text-white disabled:bg-gray-100 disabled:text-gray-300"
            disabled={isNextOrSubmitDisabled || isPending}
            onClick={isLastStep ? undefined : handleNext}
            styleType="DEFAULT_ROUNDED"
            type={isLastStep ? 'submit' : 'button'}
          >
            {isPending ? (
              <Loader iconClassName="size-5" />
            ) : (
              <AppFormattedMessage id={isLastStep ? 'ADD_TEMPLATE' : 'NEXT'} />
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};
